import { useState, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { searchArticles } from "../../api";
import { Link, useSearchParams } from "react-router-dom";
import { formatDate } from "../../utils/date";
import DOMPurify from "dompurify";

const CATEGORY_LABELS: Record<string, string> = {
  policy_regulation: "政策监管",
  product_update: "产品动态",
  market_trend: "市场趋势",
  company_news: "公司资讯",
  risk_event: "风险事件",
  technology: "技术创新",
};

const QUICK_HINTS = ["消费贷 监管", "花呗 利率", "催收 投诉", "京东 金条"];

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const initialQ = searchParams.get("q") ?? "";

  const [query, setQuery] = useState(initialQ);
  const [submittedQuery, setSubmittedQuery] = useState(initialQ);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState("");

  useEffect(() => {
    const q = searchParams.get("q") ?? "";
    if (q && q !== submittedQuery) {
      setQuery(q);
      setSubmittedQuery(q);
      setPage(1);
    }
  }, [searchParams, submittedQuery]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["search", submittedQuery, page, category],
    queryFn: () => searchArticles({ q: submittedQuery, page, page_size: 20, category: category || undefined }),
    enabled: submittedQuery.length > 0,
  });

  const handleSearch = useCallback(() => {
    if (query.trim()) {
      setSubmittedQuery(query.trim());
      setPage(1);
    }
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / 20)) : 1;

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      <section className="paper-panel-elevated overflow-hidden">
        <div className="border-b border-border px-5 py-4 sm:px-6">
          <p className="section-kicker">Search</p>
          <div className="mt-2 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl space-y-2">
              <h1 className="text-[22px] font-semibold tracking-tight text-ink sm:text-[24px]">
                信贷行业检索
              </h1>
              <p className="text-sm leading-6 text-ink-secondary">
                检索政策文件、产品动态、风险事件及行业资讯，支持全文与摘要高亮。
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-2xs text-ink-tertiary">
              <span className="rounded-full border border-border bg-surface px-2.5 py-1 font-mono tabular-nums">
                全文 + 摘要高亮
              </span>
              <span className="rounded-full border border-border bg-surface px-2.5 py-1 font-mono tabular-nums">
                分类聚合
              </span>
              <span className="rounded-full border border-border bg-surface px-2.5 py-1 font-mono tabular-nums">
                时间脉络可追溯
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-4 px-5 py-5 sm:px-6">
          <div className="flex flex-col gap-3 sm:flex-row">
            <label className="relative flex-1">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ink-faint">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <circle cx="6.5" cy="6.5" r="4.5" />
                  <line x1="10.5" y1="10.5" x2="14" y2="14" />
                </svg>
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="搜索花呗、借呗、监管政策、消费贷利率..."
                className="h-11 w-full rounded-xl border-subtle bg-surface px-11 pr-4 text-sm text-ink placeholder-ink-faint outline-none transition-colors focus:border-accent/40 focus:bg-surface-raised"
              />
            </label>
            <button
              onClick={handleSearch}
              className="h-11 rounded-xl bg-primary-700 px-5 text-sm font-medium text-white transition-colors hover:bg-primary-800 dark:bg-primary-500 dark:hover:bg-primary-600 active:scale-[0.98]"
            >
              开始检索
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-label">常用主题</span>
            {QUICK_HINTS.map((hint) => (
              <button
                key={hint}
                onClick={() => {
                  setQuery(hint);
                  setSubmittedQuery(hint);
                  setPage(1);
                }}
                className="rounded-full border border-border bg-surface px-2.5 py-1 text-2xs font-medium text-ink-secondary transition-colors hover:border-border-strong hover:text-ink"
              >
                {hint}
              </button>
            ))}
          </div>

          {submittedQuery && (
            <div className="flex flex-wrap items-center gap-2 text-2xs text-ink-tertiary">
              <span className="text-label">当前检索</span>
              <span className="rounded-full border border-accent/20 bg-accent/5 px-2.5 py-1 font-medium text-accent-dark dark:text-accent">
                {submittedQuery}
              </span>
              {category && (
                <span className="rounded-full border border-border bg-surface px-2.5 py-1 font-medium text-ink-secondary">
                  {CATEGORY_LABELS[category] ?? category}
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {!submittedQuery && (
        <section className="paper-panel px-6 py-14 text-center">
          <p className="text-sm text-ink-secondary">输入关键词开始检索</p>
          <p className="mt-2 text-xs leading-6 text-ink-tertiary">
            支持产品名、监管主题、事件关键词等。
          </p>
        </section>
      )}

      {data && (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_220px] lg:items-start">
          <div className="min-w-0 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 px-1">
              <div className="flex items-center gap-2 text-xs text-ink-secondary">
                <span>
                  找到 <span className="data-num text-sm text-ink">{data.total}</span> 条结果
                </span>
                <span className="h-3.5 w-px bg-border" />
                <span className="font-mono tabular-nums text-ink-tertiary">第 {page} / {totalPages} 页</span>
              </div>
              {(isLoading || isFetching) && (
                <span className="rounded-full border border-accent/20 bg-accent/5 px-2 py-1 text-2xs font-medium text-accent-dark dark:text-accent">
                  正在刷新结果…
                </span>
              )}
            </div>

            {data.hits.length > 0 ? (
              <div className="paper-panel overflow-hidden divide-y divide-border">
                {data.hits.map((hit) => (
                  <div key={hit.id} className="group px-5 py-4 transition-colors hover:bg-surface">
                    <div className="mb-2 flex flex-wrap items-center gap-2 text-2xs text-ink-tertiary">
                      {hit.category && (
                        <span className="rounded-full border border-border bg-surface px-2 py-0.5 font-medium text-ink-secondary">
                          {CATEGORY_LABELS[hit.category] ?? hit.category}
                        </span>
                      )}
                      {hit.source_code && <span className="font-medium text-accent-dark dark:text-accent">{hit.source_code}</span>}
                      {hit.published_at && (
                        <span className="font-mono tabular-nums text-ink-faint">{formatDate(hit.published_at)}</span>
                      )}
                    </div>

                    <Link
                      to={`/article/${hit.id}`}
                      className="block text-[15px] font-semibold leading-7 text-ink transition-colors group-hover:text-primary-600 dark:group-hover:text-primary-400"
                    >
                      {hit.highlights.title ? (
                        <span
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(hit.highlights.title[0]) }}
                          className="[&_em]:rounded-[4px] [&_em]:bg-amber-100/80 [&_em]:px-0.5 [&_em]:not-italic [&_em]:font-semibold [&_em]:text-amber-800 dark:[&_em]:bg-amber-900/30 dark:[&_em]:text-amber-300"
                        />
                      ) : (
                        hit.title
                      )}
                    </Link>

                    {(hit.highlights.summary?.[0] || hit.highlights.content_text?.[0]) && (
                      <p
                        className="mt-2 text-sm leading-6 text-ink-secondary [&_em]:rounded-[4px] [&_em]:bg-amber-100/70 [&_em]:px-0.5 [&_em]:not-italic [&_em]:font-semibold [&_em]:text-amber-800 dark:[&_em]:bg-amber-900/30 dark:[&_em]:text-amber-300"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(hit.highlights.summary?.[0] || hit.highlights.content_text?.[0] || ""),
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="paper-panel px-6 py-16 text-center">
                <p className="text-sm text-ink-secondary">未找到与「{submittedQuery}」相关的内容</p>
                <p className="mt-2 text-xs text-ink-tertiary">可以尝试更短的关键词或换个搜索角度。</p>
              </div>
            )}

            {data.total > 20 && (
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-lg border-subtle px-3 py-1.5 text-xs text-ink-secondary transition-colors hover:border-border-strong hover:text-ink disabled:opacity-40"
                >
                  上一页
                </button>
                <span className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-mono tabular-nums text-ink-tertiary">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * 20 >= data.total}
                  className="rounded-lg border-subtle px-3 py-1.5 text-xs text-ink-secondary transition-colors hover:border-border-strong hover:text-ink disabled:opacity-40"
                >
                  下一页
                </button>
              </div>
            )}
          </div>

          <aside className="space-y-4">
            {data.facets.categories.length > 0 && (
              <div className="paper-panel px-4 py-4">
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-label">分类</p>
                  <span className="text-2xs font-mono tabular-nums text-ink-faint">{data.facets.categories.length}</span>
                </div>
                <div className="space-y-1">
                  {data.facets.categories.map((f) => (
                    <button
                      key={f.key}
                      onClick={() => {
                        setCategory(f.key === category ? "" : f.key);
                        setPage(1);
                      }}
                      className={`flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-xs transition-colors ${
                        category === f.key
                          ? "border border-accent/20 bg-accent/5 font-medium text-accent-dark dark:text-accent"
                          : "text-ink-secondary hover:bg-surface hover:text-ink"
                      }`}
                    >
                      <span>{CATEGORY_LABELS[f.key] ?? f.key}</span>
                      <span className="font-mono tabular-nums text-2xs text-ink-faint">{f.count}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="paper-panel px-4 py-4">
              <p className="text-label">检索提示</p>
              <div className="mt-3 space-y-2 text-xs leading-5 text-ink-secondary">
                <p>直接输入产品名、监管主题或争议关键词即可。</p>
                <p className="text-ink-tertiary">示例：消费贷利率、抖音月付投诉、助贷监管新规</p>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
